<template>
  <div id="registration">
    <div
      id="registration-successful"
      class="registration-area"
      v-if="registrationSuccess"
    >
      <h1 class="registration-title">{{ $t(`message.thanksJoining`) }}</h1>
      <p>{{ $t(`message.account.accountCreated`) }}</p>
    </div>

    <div class="registration-area" v-else>
      <h1 class="registration-title">{{ $t(`message.registrationTitle`) }}</h1>
      <form @submit.prevent="submitForm" novalidate="true">
        <div class="row form-row">
          <HoneypotField
            :ref="this.honeypotRef"
            :fieldValue="this.user.name"
            @update:fieldValue="setHoneypotValue"
          ></HoneypotField>
          <div class="input-group col">
            <input
              type="text"
              class="form-control"
              :class="{
                'is-valid': v$.user.firstname.$dirty
                  ? !v$.user.firstname.$error
                  : null,
                'is-invalid': v$.user.firstname.$dirty
                  ? v$.user.firstname.$error
                  : null,
              }"
              :placeholder="$t(`message.firstname`)"
              v-model="user.firstname"
              @input="v$.user.firstname.$touch()"
            />
            <div class="invalid-feedback">
              {{ $t(`message.mandatoryField`) }}
            </div>
          </div>

          <div class="input-group col">
            <input
              type="text"
              class="form-control"
              :class="{
                'is-valid': v$.user.lastname.$dirty
                  ? !v$.user.lastname.$error
                  : null,
                'is-invalid': v$.user.lastname.$dirty
                  ? v$.user.lastname.$error
                  : null,
              }"
              :placeholder="$t(`message.lastname`)"
              v-model="user.lastname"
              @input="v$.user.lastname.$touch()"
            />
            <div class="invalid-feedback">
              {{ $t(`message.mandatoryField`) }}
            </div>
          </div>
        </div>

        <div class="input-group form-row">
          <input
            type="email"
            class="form-control"
            :class="{
              'is-valid': v$.user.email.$dirty ? !v$.user.email.$error : null,
              'is-invalid': v$.user.email.$dirty ? v$.user.email.$error : null,
            }"
            :placeholder="$t(`message.email`)"
            v-model="user.email"
            @input="v$.user.email.$touch()"
          />
          <div class="invalid-feedback">
            {{ $t(`message.mandatoryFieldWithFormat`) }}
          </div>
        </div>

        <div class="form-row">
          <div class="input-group password-container">
            <input
              :type="showPassword ? 'text' : 'password'"
              class="form-control"
              :class="{
                'is-valid': v$.user.password.$dirty
                  ? !v$.user.password.$error
                  : null,
                'is-invalid': v$.user.password.$dirty
                  ? v$.user.password.$error
                  : null,
              }"
              :placeholder="$t(`message.password`)"
              v-model="user.password"
              aria-describedby="pswdHelp"
              @input="v$.user.password.$touch()"
            />
            <span id="show-password" @click="showPassword = !showPassword">
              <i class="fas fa-eye" v-if="!showPassword"></i>
              <i class="fas fa-eye-slash" v-else></i>
            </span>
          </div>
          <div
            id="pswdHelp"
            class="form-text text-start"
            :class="{
              'text-danger': v$.user.password.$dirty
                ? v$.user.password.$error
                : null,
            }"
          >
            {{ $t(`message.pswdHelp`) }}
          </div>
        </div>

        <div class="form-row user-type-container">
          <div class="user-types">
            <div
              class="button"
              :class="{ active: user.type === 'student' }"
              @click="userTypeClick('student')"
            >
              {{ $t(`message.students.student`) }}
            </div>
            <div
              class="button"
              :class="{ active: user.type === 'teacher' }"
              @click="userTypeClick('teacher')"
            >
              {{ $t(`message.teachers.teacher`) }}
            </div>
          </div>

          <div
            class="user-type-invalid-feedback"
            v-if="v$.user.type.$dirty ? v$.user.type.$error : null"
          >
            {{ $t(`message.form.requiredUserType`) }}
          </div>
        </div>
        <div class="form-row input-group">
          <div class="terms-and-conditions">
            <input
              type="checkbox"
              v-model="user.conditions"
              @input="v$.user.conditions.$touch()"
            />
            {{ $t(`message.termsAccepted`) }}
            <router-link :to="{ name: 'TermsOfService' }">
              {{ $t(`message.termsAndConditions`) }} </router-link
            >.
          </div>
          <div
            v-if="v$.user.conditions.$dirty ? v$.user.conditions.$error : null"
            class="form-row input-group user-condition-invalid-feedback"
          >
            {{ $t(`message.termsAndConditionsAcceptedHelp`) }}
          </div>
        </div>
        <div class="form-row input-group">
          <div class="terms-and-conditions">
            <input type="checkbox" v-model="user.commercial" />
            {{ $t(`message.receivingCommercial`) }}
          </div>
        </div>
        <div class="privacy-policy">
          {{ $t(`message.dataSubscriptionMessage`) }}
          <a
            v-bind:href="$t(`home.PrivacyPolicy.ressourceUrl`)"
            target="_blank"
          >
            {{ $t(`message.privacyPolicyMessage`) }}
          </a>
        </div>

        <div
          class="spinner-border"
          role="status"
          v-if="$store.getters['loadings/isLoading']"
        >
          <span class="visually-hidden">{{ $t(`message.loading`) }}...</span>
        </div>

        <div class="validation_area" v-else>
          <button type="submit" class="pill">
            {{ $t(`message.submit`) }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { email, minLength, required } from "@vuelidate/validators";
import { v4 as uuidv4 } from "uuid";
import HoneypotField from "../components/HoneypotField.vue";

const hasASpecialCharacter = (string) => {
  return string.match(/[=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~`]+/) ? true : false;
};

export default {
  name: "AppRegistration",
  components: {
    HoneypotField,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      registrationSuccess: false,
      showPassword: false,
      user: {
        firstname: "",
        lastname: "",
        email: "",
        password: "",
        type: "",
        conditions: "",
        commercial: false,
      },
      honeypotRef: "",
    };
  },
  validations: {
    user: {
      firstname: { required },
      lastname: { required },
      email: { required, email },
      password: { required, minLength: minLength(8), hasASpecialCharacter },
      type: { required },
      conditions: { checked: (value) => value === true },
    },
  },
  methods: {
    getHoneypotRef() {
      this.honeypotRef = uuidv4();
    },
    setHoneypotValue(value) {
      this.user.name = value;
    },
    submitForm() {
      this.v$.$touch();
      var isValidHoneypot = this.$refs[this.honeypotRef].validate();

      if (!this.v$.$invalid) {
        if (isValidHoneypot) {
          this.$store
            .dispatch("users/createUser", this.user)
            .then((response) => {
              if (response.status == 200) {
                this.$router.push({ name: "HomePage" });
                this.$store.dispatch("notifications/addSuccess", {
                  message: `${this.$t(
                    `message.notifications.account.pendingAccountExists`,
                  )}
                    ${this.$t(`message.notifications.validationMailResent`)}`,
                });
              } else {
                this.registrationSuccess = true;
              }
            })
            .catch((error) => {
              if (error.response.status == 500) {
                this.$store.dispatch("notifications/addError", {
                  message: this.$t(`message.notifications.signupError`),
                });
              } else if (error.response.status == 409) {
                // Valid account already exists
                this.$store.dispatch("notifications/addError", {
                  message: this.$t(`message.notifications.emailExists`),
                });
              } else if (error.response.status == 422) {
                // Valid account already exists
                this.$store.dispatch("notifications/addError", {
                  message: this.$t(`message.form.invalidForm`),
                });
              }
            })
            .finally(() => {
              this.$store.dispatch("loadings/disableLoadings");
            });
        } else {
          this.$store.dispatch("notifications/addError", {
            message: this.$t(`message.form.invalidForm`),
          });
        }
      }
    },
    userTypeClick(type) {
      this.user.type = type;
      this.v$.user.type.$touch();
    },
  },
  mounted() {
    if (import.meta.env.VITE_GAR == "true") {
      this.$router.push({ name: "HomePage" });
    } else {
      this.$store.dispatch("users/logout");
    }
    this.getHoneypotRef();
  },
};
</script>

<style lang="scss" scoped>
#registration {
  position: relative;
  display: flex;
  min-height: calc(100vh - 120px - 126px - 40px);
  margin-bottom: 20px;

  .registration-area {
    margin: 0 auto;
    padding: 3em 1em;
    width: 93%;
    border-radius: 50px;
    background-color: $primaryColorLight;
    text-align: center;
    height: 100%;

    .terms-and-conditions {
      text-align: left;
    }

    .form-row {
      margin-bottom: 1.5rem;
    }

    .registration-title {
      font-family: "Averia Sans Libre", sans-serif;
      color: $primaryColor;
      margin-bottom: 50px;
    }

    @media screen and (min-width: 993px) {
      form {
        margin: 0 auto;
        width: 50%;
      }
    }

    .password-container {
      position: relative;
      width: 100%;

      input {
        background-image: none !important;
      }

      #show-password {
        position: absolute;
        right: 12px;
        top: 8px;
        color: grey;
        z-index: 10;

        &:hover {
          cursor: pointer;
          color: black;
        }
      }
    }

    .invalid-feedback {
      text-align: left;
    }

    input {
      box-shadow: none !important;
      max-height: 2.5rem;
      border-radius: 0.25rem !important;
    }

    .user-type-container {
      height: 65px;

      .user-types {
        display: flex;
        justify-content: center;

        .button {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 200px;
          border: 3px solid $secondaryColor;
          background-color: $whiteColor;
          color: $secondaryColor;
          height: 40px;
          border-radius: 20px;
          margin: 0px 10px 0px 10px;

          &:hover {
            cursor: pointer;
            color: $whiteColor;
            background-color: $secondaryColor;
          }

          &.active {
            color: $whiteColor;
            background-color: $secondaryColor;
          }
        }
      }

      .user-type-invalid-feedback {
        text-align: left;
        font-size: 0.875em;
        margin-top: 0.25rem;
        color: #dc3545;
      }
    }

    .validation_area {
      display: flex;
      flex-direction: column;
      align-items: center;

      .pill {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100px;
        border: 3px solid $secondaryColor;
        background-color: $secondaryColor;
        color: $whiteColor;
        height: 40px;
        border-radius: 20px;

        &:hover {
          border: 3px solid $secondaryColor;
          color: $secondaryColor;
          background-color: $backgroundColor;
        }
      }
    }

    .user-condition-invalid-feedback {
      text-align: left;
      font-size: 0.875em;
      margin-top: 0.25rem;
      color: #dc3545;
    }

    .privacy-policy {
      margin: 1rem;
      text-align: center;
      font-size: 0.8rem;
      a {
        color: inherit;
      }
    }
  }
}
</style>
